
// import CustomFormCompInput from './CustomFormCompInput.vue';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import inputProps from './custom_form_comp_input_props';

const inputPropNames = Object.keys(inputProps);  // string names of all the input props for CustomFormCompInput

export default {
    name: 'CustomFormSchema',
    components: {
        //CustomFormCompInput,
    },
    props: {
        name: {  // leave blank for ROOT schema, but must use something for group inputs (name of the group field)
            type: String,
            required: false,  // was true before aug 27
        },
        schema: {
            type: Array,
            default: () => [],
        },
        /* context: {  // use provide/inject instead - pass in from form, pass on to all children?
            type: Object,
            default: () => {},
        }, */
        initialData: {  // only works for INITIAL data, not reactive
            type: Object,
            default: () => {},
        },
        level: {  // 0 is root, 1 is first level of nesting - group field will have 0, but its nested schema will be 1, which you can pass directly to children here
            type: Number,
            default: 0,
        },
        disabled: {  // disable the ENTIRE THING?  read-only, all fields
            // allow this to be json-logic conditional object???
            type: Boolean,
            default: false,
        },
        displayMode: {  // read-only, non-form-looking mode -- will also set disabled locally
            type: Boolean,
            default: false,
        },
        // need something like 'allowEditMode'?
        rowIndex: { 
            // pass in the rowIndex from parent - then undefined means not a row 
            type: Number,
            required: false,
        },
    },
    data() {
        return {
            formData: {},
            registry: {},
            // validation flags - run through all children, consolidate flags from each into these...?
            allPassed: null,
            allValid: null,  
            debounceTime: 100,
        }
    },
    computed: {
        localDisabled() {
            return this.disabled || this.displayMode;
        },
    },
    created() {
        //console.log(`*** FROM CREATED OF FORM SCHEMA: ${this.name || 'ROOT'}`);
        // this only gets called on actual created, not on any re-render.... render() gets called on re-renders, when schema changes
        //const data = Object.assign({}, (this.initialData ? this.initialData : {}));
        this.formData = this.initialData ? cloneDeep(this.initialData) : {};
        // this.schema.forEach(sch => {
        //     data[sch.path] = '';  // actually, don't do any of this... just honor the initialData, if any 
        //     //this.$set(this.formData, sch.path, '');  // or whatever they consider as their empty value 
        // });
        // make debounced parent update method
        this.debouncedHandleUpdate = debounce(this.handleUpdate, this.debounceTime);
    },
    mounted() {
        // when this schema mounts, register it in the form or group input registry
        this.$emit('mounted', this);
    },
    beforeDestroy() {
        // clear the debounced thing
        this.debouncedHandleUpdate.cancel();  // also has a .flush() method to invoke any waiting calls
    },
    provide: function() {
        // the ROOT FORM only gets data after schema is done with all its stuff, so it might be stale
        // so we provide the ROOT DATA here, IF this is the root schema (level == 0) 
        // each schema provides a method to get the immediate parent data (useful for rows)
        const providers = {
            getParentData: () => {
                return this.formData;
            },
        };
        if (this.level === 0) {
            // is this the ROOT schema?  if so, provide the root data 
            providers['getRootData'] = () => {
                return this.formData;
            }
        }
        return providers;
    },
    methods: {
        // PUBLIC METHODS - CAN CALL FROM PARENT
        setData(data) {
            // TODO - allow MERGING data (only change the fields we pass in, OR completely replacing)
            // and/or just set the fields we have, and use some `setValue` method on them, so ONLY THOSE update their validation...
            console.log('HELLO FROM setData IN FORM SCHEMA', data);
            // set new data, can call from parent
            // set new this.formData, which should cause re-render, which should emit updates? and they get handled here?
            this.formData = data;  // don't think we need to cloneDeep? 
            // call handleUpdate - NON-debounced - to update all the things -- in nextTick?
            this.$nextTick(() => {  // not sure if we need nextTick...
                this.handleUpdate();
            })
        },
        // PRIVATE / INTERNAL METHODS
        emitUpdate() {
            const payload = {
                data: {...this.formData},  // I *think* a shallow clone is all we need... nested schemas will shallow-copy themselves
                valid: this.allValid,
                passed: this.allPassed,
            };  // maybe should cloneDeep instead....
            this.$emit('update', payload);
        },
        updateSelf() {  // call this updateSchemaData instead?
            // run through all registered children, see if they are valid - if they are hidden/removed, treat differently
            let passed = [];
            let valid = [];
            let data = {};
            Object.values(this.registry).forEach(childComp => {
                // check conditionals on child first?  hidden / removed
                childComp.evaluateConditionals();
                // console.log('updateSelf on childComp', {
                //     name: childComp.name, 
                //     value: childComp.currentValue,
                //     passed: childComp.passed, 
                //     valid: childComp.valid,
                //     removed: childComp.isRemoved,
                // });
                // if (childComp.isRemoved && childComp.name in this.formData) {
                //     // if child is removed, we need to remove from this.formData IF IT IS THERE
                //     this.$delete(this.formData, childComp.name);
                // }
                if (!childComp.isRemoved) {
                    // also only add it if it is valid?  no... or else we'll block progress towards valid inputs
                    // only add it if it was touched?  has initialValue? 
                    data[childComp.name] = childComp.currentValue;
                }
                // TODO - handle un-removing a field -- field will still have its value, but we need to add it back...
                passed.push(childComp.passed);
                valid.push(childComp.valid);
            });
            this.formData = data;  // set entirely new data!  ok?
            this.allPassed = passed.every(p => !!p);
            this.allValid = valid.every(v => !!v);
        },
        handleUpdate() {
            // NON-DEBOUNCED -- use the debounced version 
            // ORDER MATTERS HERE - if you get root data from FORM, need to emitUpdate before running validation
            this.updateSelf();
            this.emitUpdate();
        },
        // EVENT HANDLERS FROM CHILD FIELDS THAT WE BUILD UP HERE
        handleFieldCustomInput(payload) {
            const { field, value } = payload;
            //console.log('hello from handleFieldCustomInput', field, value);
            // if your handleUpdate method gets values from all fields, then we don't need to bother with $set() here
            // but keep it.... because we may want that new value in formData when other fields get evaluateConditionals called!
            console.log(`from HANDLE FIELD CUSTOM INPUT on schema ${this.name || 'ROOT'}`, field, value);
            // DISABLED FOR NOW - using handleFieldInput
            //this.$set(this.formData, field, value);
            //this.debouncedHandleUpdate();  // debounce or not?
            // // ORDER MATTERS HERE - if you get root data from FORM, need to emitUpdate before running validation
            // this.updateSelf();
            // this.emitUpdate();
        },
        handleFieldInput(fieldName, payload) {
            // from the @input event on child inputs - attached in listeners below 
            // payload should be the new value 
            console.log(`from HANDLE FIELD INPUT on schema ${this.name || 'ROOT'} - fieldName, payload`, fieldName, payload);
            this.$set(this.formData, fieldName, payload);
            // do the things below via debounced call 
            this.debouncedHandleUpdate();  // debounce or not?
        },
        handleFieldBlur(payload) {  // move things here if needed for performance... but try not to first
            console.log('hello from handleFieldBlur', payload); 
        },
        handleChildInputMounted(childComp) {
            console.log('in SCHEMA - will register child input', childComp);
            this.$set(this.registry, childComp.name, childComp);
        },
        relayChildComponentEvent(evtName, evtPayload, ...args) {
            // this is the generic event handler for any non-input component, we just relay the payload up to top level and emit it
            // maybe also pass up the component itself?  or some ref/id to it?
            console.log('relaying event from child component up to emit from top level in CustomFormSchema!', evtName, evtPayload, ...args);
            this.$emit(evtName, evtPayload);
        },

    },
    render: function (createElement) {
        // DO WE HAVE A FORM INPUT OR NORMAL COMPONENT?
        console.log('STARTING FORM SCHEMA RE-RENDER WITH name =', this.name || "ROOT");
        const formData = cloneDeep(this.formData);  // grab cloned copy of formData whenever we re-render... need it to set values for each field 
        const createInputElement = (schItem) => {
            // if NOT a component, its a form input... build it
            const { name, ...schProps } = schItem;
            const inputListeners = {
                'custom-input': (event) => {  // YES!  arrow functions work - they handle the 'this' correctly 
                    this.handleFieldCustomInput(event);
                },
                // what about actual @input event???  use that instead?  can bake the field name into it...
                input: (event) => {
                    this.handleFieldInput(name, event);
                },
                blur: (event) => {
                    this.handleFieldBlur(event);
                },
                mounted: (comp) => {
                    this.handleChildInputMounted(comp);
                },
            };
            // need to pull off some things that get added as top-level things in createElement:  class, style, maybe more....  maybe change the structure of all this
            // gotta do stuff in funky way with 'class' since its a reserved keyword
            const createElementParams = {};  // the data obj that we pass to createElement() -- build it as we go
            if ('class' in schProps) {
                createElementParams['class'] = schProps.class;
                delete schProps.class;  // so its not added to 'props' key in createElement
            }
            //const newName = `${this.name}.${name}`;
            //const compProps = { parentContext: this.context, name: newName, ...schProps };  // NOTE: passing a single context object might not work? or maybe OK since its nested in props:{} - see https://github.com/vuejs/vue/issues/12240
            const compProps = { name: name, level: this.level, disabled: this.localDisabled, displayMode: this.displayMode };  // add these, then add from the schItem
            const compAttrs = {};  // separate out which ones are props, which are attrs -- some components like PrimeVue ones this matters
            for (const p in schProps) {
                if (inputPropNames.includes(p)) {
                    compProps[p] = schProps[p];
                } else {
                    compAttrs[p] = schProps[p];
                }
            }
            // grab the current value for this field (if any) from formData
            if (name in formData) {
                compProps['value'] = formData[name];  // should not need to clone it, even if its a complex data structure, because we did a cloneDeep at the start of re-render
                console.log('WE HAVE VALUE FOR FIELD', name, formData[name]);
            }
            console.log('Will make input with these props:', compProps);
            console.log('Will make input with these attrs:', compAttrs);
            createElementParams['props'] = compProps;
            createElementParams['attrs'] = compAttrs;
            createElementParams['on'] = inputListeners;
            createElementParams['ref'] = name;  // use same name for ref... same as used in registry
            //return createElement('CustomFormCompInput', { props: compProps, on: inputListeners });
            return createElement('CustomFormCompInput', createElementParams);
        };
        const createElementRecursive = (schItem) => {
            // THE RECURSIVE FUNCTION!
            //console.log('starting createElementRecursive !!!');
            // check if we have a form input schema item OR a non-input component / HTML element
            const isNonInputElement = 'component' in schItem;
            if (!isNonInputElement) {
                console.log('CREATING INPUT ELEMENT IN SCHEMA RE-RENDER WITH name =', schItem.name);
                return createInputElement(schItem);
            }
            // otherwise... we have 'component' then we are rendering a vue component OR a normal HTML component
            // FYI https://v2.vuejs.org/v2/guide/render-function#The-Data-Object-In-Depth
            // just pass stuff from backend in the same structure?
            // for 'on' event handelers, make a way to 'relay' them up to top level?  using ref/id of the component / element?  or only if you have a ref?
            const compName = schItem.component;  // used in caller....
            //console.log('CREATING COMPONENT', compName);
            const compDataObj = {};  // build this up as we go...
            if (schItem.class) {  // can be a string, object, or array, just like normal  (can't call the variable 'class' though)
                compDataObj['class'] = schItem.class;
            }
            if (schItem.style) {  
                compDataObj['style'] = schItem.style;
            }
            if (schItem.attrs) { 
                compDataObj['attrs'] = schItem.attrs;
            }
            if (schItem.props) {  
                compDataObj['props'] = schItem.props;
            }
            if (schItem.domProps) {  
                compDataObj['domProps'] = schItem.domProps;
            }
            if (schItem.key) {  
                compDataObj['key'] = schItem.key;
            }
            if (schItem.ref) {  // must also pass refInFor: true if its in an array
                compDataObj['ref'] = schItem.ref;
            }
            // add directives, scopedSlots, slot, nativeOn later if needed
            if (schItem.on) {  // should be an object with keys being the event names.... 
                // look at vue formulate for reference here... they have good stuff for this
                // this just passes up a static payload for now...??? not great
                // maybe if value is boolean (and true) then pass it up... if a string, call it on the root form?  but must pass that instance down into schema since schema is not top level
                // or if value is a string or non-boolean, just use that as the event payload to pass up???
                const listeners = {};
                Object.entries(schItem.on).forEach(([evtName, evtPayload]) => {
                    listeners[evtName] = (...args) => {
                        // ...args is the payload of the possibly custom event...
                        // check type of evtPayload???
                        this.relayChildComponentEvent(evtName, evtPayload, ...args);  // what to do with extra args?
                    }; 
                });
                compDataObj['on'] = listeners;
            }
            //console.log(`compDataObj for ${compName}`, compDataObj);
            // now do it recursively for children, if any -- they get created 'before' the parent is created
            if (schItem.children) {
                if (Array.isArray(schItem.children)) {
                    // if its an array of children, we have child elements to create
                    const childElements = schItem.children.map(ch => createElementRecursive(ch));
                    return createElement(compName, compDataObj, childElements);
                } else {
                    // otherwise we should have a single string... its a text VNode (could be multiple strings???)
                    return createElement(compName, compDataObj, schItem.children);
                }
            }
            // still going?  then we did not have any children
            return createElement(compName, compDataObj);
        };
        return createElement(
            'div',   // tag name - will wrap all fields in this at TOP LEVEL
            this.schema.map((schItem) => {
                return createElementRecursive(schItem);
                // if ('component' in schItem) {
                //     const compName = schItem.component;
                //     /// TODO - make components recursively
                //     const compDataObj = createCompDataObj(schItem);
                //     return createElement(compName, {...compDataObj}); // cloneDeep?
                // }
                // // if NOT a component, its a form input... build it
                // const { name, ...schProps } = schItem;
                // //const newName = `${this.name}.${name}`;
                // //const compProps = { parentContext: this.context, name: newName, ...schProps };  // NOTE: passing a single context object might not work? or maybe OK since its nested in props:{} - see https://github.com/vuejs/vue/issues/12240
                // const compProps = { name: name, level: this.level, ...schProps };
                // console.log('Will make input with these props:', compProps);
                // return createElement('CustomFormCompInput', { props: compProps, on: inputListeners });
            })
        )
    },
}